import { useEffect, useState, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  doc, collection, updateDoc, serverTimestamp, writeBatch, addDoc,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { ActivityMatches } from '../../components/TeamFixtures';
import SquadCard from '../../components/SquadCard';
import { formatDob } from '../../components/TeamsTab';
import { teamsWithAges, squadsWithIds } from '../../utils/teamFixtures';
import { usePreviewNavigate } from '../../contexts/PreviewContext';
import { DEFAULT_PERMISSIONS } from '../../utils/supplierPermissions';
import { useSupplierViews, mergeViews } from '../../hooks/useSupplierPortal';
import { useT } from '../../contexts/LocaleContext';
import { LanguageSwitch } from '../../components/LanguageSwitch';

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

/** "Sun 1 Nov" (or "dom, 1 nov") for itinerary day N (1-based) of a tour, or '' without dates. */
function dayDate(departureDate, day, t) {
  if (!departureDate || !day) return '';
  const d = new Date(`${String(departureDate).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + Number(day) - 1);
  return d.toLocaleDateString(t.dateLocale, { weekday: 'short', day: 'numeric', month: 'short' });
}

function dayLabel(departureDate, day, t) {
  if (!day) return t('supplier.dayTbc');
  const date = dayDate(departureDate, day, t);
  return date ? `${t('supplier.day', { n: day })} · ${date}` : t('supplier.day', { n: day });
}

/** An activity's type in the chosen language (unknown ones stay as typed). */
function activityName(type, t) {
  return type ? t.value('activity', type) : type;
}

function SectionCard({ title, children }) {
  return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] overflow-hidden">
      <div className="px-5 py-3 bg-[#F9FAFB] border-b border-[#E5E7EB]">
        <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.9px]">{title}</p>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-0.5">{label}</p>
      <p className="text-[13px] text-[#111827] [overflow-wrap:anywhere]">{value}</p>
    </div>
  );
}

// ── Stage 1: Booking confirmation per assignment ──────────────────────────────

function BookingAssignmentRow({ assignment, onUpdated }) {
  const t = useT();
  const [responding, setResponding] = useState(false);
  const [action, setAction] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [invoiceProgress, setInvoiceProgress] = useState(0);
  const fileRef = useRef(null);

  const isPending = assignment.status === 'pending';

  async function submit() {
    if (!action) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'bookingSuppliers', assignment.id), {
        status: action === 'confirm' ? 'confirmed' : 'declined',
        supplierNotes: note.trim(),
        respondedAt: serverTimestamp(),
        confirmedAt: action === 'confirm' ? serverTimestamp() : null,
      });
      setResponding(false);
      setNote('');
      setAction(null);
      if (onUpdated) onUpdated();
    } finally {
      setSaving(false);
    }
  }

  async function handleInvoiceUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingInvoice(true);
    try {
      const path = `invoices/${assignment.requestId}/${assignment.id}/${Date.now()}_${file.name}`;
      const ref2 = storageRef(storage, path);
      await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(ref2, file);
        task.on('state_changed',
          (snap) => setInvoiceProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          reject,
          resolve
        );
      });
      const url = await getDownloadURL(ref2);
      await updateDoc(doc(db, 'bookingSuppliers', assignment.id), {
        invoiceUrl: url,
        invoiceFileName: file.name,
        invoiceUploadedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error(err);
      alert(t('supplier.booking.invoiceFailed'));
    } finally {
      setUploadingInvoice(false);
      setInvoiceProgress(0);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  const statusStyles = {
    pending:   'bg-[#FFF7ED] text-[#92400E]',
    confirmed: 'bg-[#ECFDF5] text-[#065F46]',
    declined:  'bg-[#FEE2E2] text-[#991B1B]',
  };
  const statusDot = {
    pending:   'bg-[#F59E0B]',
    confirmed: 'bg-[#10B981]',
    declined:  'bg-[#C0392B]',
  };
  const statusLabels = {
    pending: t('supplier.booking.statuses.pending'),
    confirmed: t('supplier.booking.statuses.confirmed'),
    declined: t('supplier.booking.statuses.declined'),
  };

  return (
    <div className="border border-[#E5E7EB] rounded-[10px] p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[13px] font-bold text-[#111827]">
            {(Array.isArray(assignment.serviceTypes) && assignment.serviceTypes.length
              ? assignment.serviceTypes.map((x) => activityName(x, t)).join(', ')
              : activityName(assignment.serviceType, t)) || t('supplier.booking.service')}
          </p>
          {assignment.staffNotes && (
            <p className="text-[12px] text-[#9CA3AF] italic mt-0.5">"{assignment.staffNotes}"</p>
          )}
        </div>
        <span className={`inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[12px] text-[11px] font-semibold whitespace-nowrap flex-shrink-0 ${statusStyles[assignment.status] || 'bg-[#F3F4F6] text-[#6B7280]'}`}>
          <span className={`w-[5px] h-[5px] rounded-full flex-shrink-0 ${statusDot[assignment.status] || 'bg-[#9CA3AF]'}`} />
          {statusLabels[assignment.status] || assignment.status}
        </span>
      </div>

      {isPending && !responding && (
        <div className="flex gap-2">
          <button
            onClick={() => { setAction('confirm'); setNote(''); setResponding(true); }}
            className="px-[16px] py-[8px] text-[13px] font-semibold text-white bg-sg hover:bg-sg/90 rounded-[8px] transition"
          >
            {t('supplier.booking.confirmBooking')}
          </button>
          <button
            onClick={() => { setAction('decline'); setNote(''); setResponding(true); }}
            className="px-[16px] py-[8px] text-[13px] font-semibold text-dr border border-dr/30 hover:bg-red-light rounded-[8px] transition"
          >
            {t('supplier.booking.decline')}
          </button>
        </div>
      )}

      {responding && (
        <div className="space-y-2">
          <label className="block text-[12px] font-medium text-[#374151]">
            {action === 'confirm' ? t('supplier.booking.noteConfirm') : t('supplier.booking.noteDecline')}
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder={action === 'confirm' ? t('supplier.booking.phConfirm') : t('supplier.booking.phDecline')}
            className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb resize-none"
          />
          <div className="flex gap-2">
            <button
              onClick={submit}
              disabled={saving}
              className={`px-[16px] py-[8px] text-[13px] font-semibold text-white rounded-[8px] transition disabled:opacity-60 ${action === 'confirm' ? 'bg-sg hover:bg-sg/90' : 'bg-dr hover:bg-dr/90'}`}
            >
              {saving ? t('supplier.booking.saving') : action === 'confirm' ? t('supplier.booking.confirm') : t('supplier.booking.decline')}
            </button>
            <button onClick={() => { setResponding(false); setAction(null); }}
              className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151] transition">
              {t('supplier.booking.cancel')}
            </button>
          </div>
        </div>
      )}

      {assignment.status !== 'pending' && assignment.supplierNotes && (
        <p className="text-[12px] text-[#9CA3AF] italic">{t('supplier.booking.yourNote', { note: assignment.supplierNotes })}</p>
      )}

      {/* Invoice upload */}
      <div className="border-t border-[#F3F4F6] pt-3">
        <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-2">{t('supplier.booking.invoice')}</p>
        {assignment.invoiceUrl ? (
          <div className="flex items-center gap-3">
            <a href={assignment.invoiceUrl} target="_blank" rel="noopener noreferrer"
              className="text-[12.5px] font-bold text-pb hover:text-navy transition">
              {assignment.invoiceFileName || t('supplier.booking.viewInvoice')} ↗
            </a>
            <label className="text-[12px] text-[#9CA3AF] hover:text-[#374151] cursor-pointer transition">
              {t('supplier.booking.replace')}
              <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xls,.doc,.docx" onChange={handleInvoiceUpload} className="hidden" />
            </label>
          </div>
        ) : (
          <label className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-pb hover:text-navy transition cursor-pointer">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            {uploadingInvoice ? t('supplier.booking.uploading', { n: invoiceProgress }) : t('supplier.booking.uploadInvoice')}
            <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xls,.doc,.docx" onChange={handleInvoiceUpload} className="hidden" disabled={uploadingInvoice} />
          </label>
        )}
      </div>
    </div>
  );
}

// ── Stage 2: Itinerary approval ───────────────────────────────────────────────

function ItineraryApprovalSection({ token, onDone, departureDate }) {
  const t = useT();
  const [chosen, setChosen] = useState(null);
  const [question, setQuestion] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(act) {
    setSaving(true);
    try {
      const batch = writeBatch(db);
      const newStatus = act === 'approved' ? 'approved' : act === 'declined' ? 'declined' : 'question';
      for (const itemId of token.itemIds) {
        batch.update(doc(db, 'requests', token.requestId, 'itinerary', itemId), {
          approvalStatus: newStatus,
          respondedAt: serverTimestamp(),
          ...(act === 'question' ? { questionText: question } : {}),
        });
      }
      batch.update(doc(db, 'supplierApprovalTokens', token.id), {
        used: true,
        usedAt: serverTimestamp(),
        response: act,
        ...(act === 'question' ? { question } : {}),
      });
      await batch.commit();
      onDone();
    } catch {
      alert(t('supplier.approve.failed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="bg-[#F9FAFB] rounded-[10px] border border-[#E5E7EB] overflow-hidden">
        <div className="px-4 py-2 bg-[#F3F4F6] border-b border-[#E5E7EB]">
          <p className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-[0.9px]">{t('supplier.approve.items')}</p>
        </div>
        <div className="divide-y divide-[#F3F4F6]">
          {(token.items || []).map((item) => (
            <div key={item.id} className="px-4 py-3 flex items-start gap-3">
              <div className="min-w-[48px] text-center flex-shrink-0">
                <p className="text-[11px] font-bold text-[#6B7280]">
                  {dayLabel(departureDate, item.day ?? (item.dayIdx != null ? item.dayIdx + 1 : null), t)}
                </p>
                <p className="text-[12px] font-semibold text-[#374151]">{item.time || '—'}</p>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-[#111827]">{item.title || activityName(item.activityType, t)}</p>
                {item.notes && <p className="text-[12px] text-[#6B7280] mt-0.5 italic">{item.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {chosen !== 'question' ? (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => submit('approved')} disabled={saving}
            className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-sg hover:bg-sg/90 rounded-[8px] transition disabled:opacity-60">
            {saving ? t('supplier.approve.saving') : t('supplier.approve.approveAll')}
          </button>
          <button onClick={() => submit('declined')} disabled={saving}
            className="px-[18px] py-[9px] text-[13px] font-semibold text-dr border border-dr/30 hover:bg-red-light rounded-[8px] transition disabled:opacity-60">
            {t('supplier.approve.decline')}
          </button>
          <button onClick={() => setChosen('question')} disabled={saving}
            className="px-[18px] py-[9px] text-[13px] font-semibold text-[#374151] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-[8px] transition disabled:opacity-60">
            {t('supplier.approve.question')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-[13px] text-[#374151] font-medium">{t('supplier.approve.whatQuestion')}</p>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={3}
            placeholder={t('supplier.approve.questionPh')}
            className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb resize-none"
          />
          <div className="flex gap-2">
            <button onClick={() => submit('question')} disabled={!question.trim() || saving}
              className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
              {saving ? t('supplier.approve.sending') : t('supplier.approve.send')}
            </button>
            <button onClick={() => setChosen(null)} className="px-4 py-2.5 text-[13px] text-[#6B7280] hover:text-[#374151]">
              {t('supplier.approve.back')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Participants read-only view ───────────────────────────────────────────────

const NO_REQ_VALUES = new Set([
  'none','n/a','na','no','nil','-','not applicable','no allergies',
  'no dietary requirements','none.','no.','n/a.','not applicable.',
]);
function hasReq(v) { return !!v && !NO_REQ_VALUES.has(v.trim().toLowerCase()); }

function fmtDate(str) {
  if (!str) return '—';
  const parts = str.split('-');
  if (parts.length !== 3) return str;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function PRow({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-[0.8px]">{label}</p>
      <p className="text-[12px] text-[#111827] mt-0.5 leading-snug">{value}</p>
    </div>
  );
}

function exportParticipantsCsv(participants, tourName, withPassports, t) {
  const h = (k) => t(`supplier.participants.csv.${k}`);
  const columns = [
    [h('fullName'), (p) => p.name],
    [h('travellerType'), (p) => p.travellerType],
    [h('dob'), (p) => fmtDate(p.dateOfBirth)],
    [h('gender'), (p) => t.value('gender', p.gender)],
    [h('nationality'), (p) => p.nationality],
    ...(withPassports ? [
      [h('passportNo'), (p) => p.passportNumber],
      [h('passportExpiry'), (p) => fmtDate(p.passportExpiry)],
      [h('countryOfIssue'), (p) => p.countryOfIssue],
    ] : []),
    [h('allergies'), (p) => p.allergies],
    [h('dietary'), (p) => p.dietaryRequirements],
    [h('medical'), (p) => p.conditions],
    [h('safety'), (p) => p.safetyInfo],
    ...(withPassports ? [
      [h('contactPhone'), (p) => p.contactPhone],
      [h('contactEmail'), (p) => p.contactEmail],
      [h('guardianName'), (p) => p.guardianName],
      [h('guardianPhone'), (p) => p.guardianPhone],
      [h('guardianEmail'), (p) => p.guardianEmail],
    ] : []),
  ];
  const headers = [h('no'), ...columns.map(([label]) => label)];
  const rows = participants.map((p, i) => [i + 1, ...columns.map(([, get]) => (get(p) === '—' ? '' : get(p) || ''))]);
  const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
  const csv = [headers, ...rows].map((r) => r.map(escape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `participants-${(tourName || 'tour').replace(/[^a-z0-9]/gi, '-').toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function ParticipantsList({ participants, tourName, withPassports }) {
  const t = useT();
  const [expandedId, setExpandedId] = useState(null);

  if (participants.length === 0) return <p className="text-[13px] text-[#9CA3AF]">{t('supplier.participants.none')}</p>;

  const flagged = participants.filter(
    (p) => hasReq(p.allergies) || hasReq(p.dietaryRequirements) || hasReq(p.conditions) || hasReq(p.safetyInfo)
  );

  return (
    <div className="space-y-4">

      {/* Export button + count */}
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-[#6B7280]">{participants.length === 1 ? t('supplier.participants.countOne') : t('supplier.participants.countMany', { n: participants.length })}</p>
        <button
          onClick={() => exportParticipantsCsv(participants, tourName, withPassports, t)}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-pb hover:text-navy border border-pb/20 hover:border-pb/40 px-3 py-1.5 rounded-[8px] transition"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
          </svg>
          {t('supplier.participants.export')}
        </button>
      </div>

      {/* Requirements summary banner */}
      {flagged.length > 0 && (
        <div className="bg-amber-light border border-ba/25 rounded-[12px] overflow-hidden">
          <div className="px-4 py-3 border-b border-ba/20 flex items-center gap-2">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <div>
              <p className="text-[11px] font-bold text-[#92400E] uppercase tracking-[0.9px]">{t('supplier.participants.reqTitle')}</p>
              <p className="text-[12px] text-[#92400E]/80 mt-0.5">{flagged.length === 1 ? t('supplier.participants.reqCountOne') : t('supplier.participants.reqCountMany', { n: flagged.length })}</p>
            </div>
          </div>
          <div className="divide-y divide-ba/10">
            {flagged.map((p) => (
              <div key={p.id} className="px-4 py-3">
                <p className="text-[13px] font-semibold text-[#111827]">{p.name || '—'}</p>
                {hasReq(p.allergies)            && <p className="text-[12px] text-[#92400E] mt-0.5"><strong>{t('supplier.participants.allergies')}</strong> {p.allergies}</p>}
                {hasReq(p.dietaryRequirements)  && <p className="text-[12px] text-[#374151] mt-0.5"><strong>{t('supplier.participants.dietary')}</strong> {p.dietaryRequirements}</p>}
                {hasReq(p.conditions)           && <p className="text-[12px] text-[#374151] mt-0.5"><strong>{t('supplier.participants.medical')}</strong> {p.conditions}</p>}
                {hasReq(p.safetyInfo)           && <p className="text-[12px] text-[#92400E] mt-0.5"><strong>{t('supplier.participants.safety')}</strong> {p.safetyInfo}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full participant list — expandable */}
      <div className="space-y-2">
        {participants.map((p, i) => {
          const name = p.name || t('supplier.participants.participantN', { n: i + 1 });
          const isFlagged = hasReq(p.allergies) || hasReq(p.dietaryRequirements) || hasReq(p.conditions) || hasReq(p.safetyInfo);
          const isExpanded = expandedId === p.id;
          return (
            <div key={p.id} className="bg-white border border-[#E5E7EB] rounded-[10px] overflow-hidden">
              <button
                onClick={() => setExpandedId(isExpanded ? null : p.id)}
                className="w-full flex items-center justify-between px-4 py-3 text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-6 h-6 rounded-full bg-[#F3F4F6] flex items-center justify-center text-[11px] font-bold text-[#9CA3AF] flex-shrink-0">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-[#111827] truncate">{name}</p>
                    <p className="text-[11px] text-[#6B7280] mt-0.5">
                      {[p.travellerType, p.dateOfBirth ? t('supplier.participants.dobShort', { d: fmtDate(p.dateOfBirth) }) : null].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  {isFlagged && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-light text-[#92400E]">
                      {t('supplier.participants.requirements')}
                    </span>
                  )}
                  <svg className={`w-4 h-4 text-[#9CA3AF] transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 pt-3 border-t border-[#F3F4F6] space-y-4">

                  <div>
                    <p className="text-[10px] font-bold text-[#6B7280] uppercase tracking-[0.9px] mb-2">{t('supplier.participants.personal')}</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                      <PRow label={t('supplier.participants.fullName')}    value={name} />
                      <PRow label={t('supplier.participants.dob')}         value={fmtDate(p.dateOfBirth)} />
                      <PRow label={t('supplier.participants.gender')}      value={t.value('gender', p.gender)} />
                      <PRow label={t('supplier.participants.nationality')} value={p.nationality} />
                    </div>
                  </div>

                  {withPassports && (
                  <div>
                    <p className="text-[10px] font-bold text-[#6B7280] uppercase tracking-[0.9px] mb-2">{t('supplier.participants.passport')}</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                      <PRow label={t('supplier.participants.passportNo')}     value={p.passportNumber} />
                      <PRow label={t('supplier.participants.expiry')}         value={fmtDate(p.passportExpiry)} />
                      <PRow label={t('supplier.participants.countryOfIssue')} value={p.countryOfIssue} />
                    </div>
                  </div>
                  )}

                  {(hasReq(p.allergies) || hasReq(p.dietaryRequirements) || hasReq(p.conditions) || hasReq(p.safetyInfo)) && (
                    <div className="bg-amber-light rounded-[8px] p-3 space-y-1.5">
                      <p className="text-[10px] font-bold text-[#92400E] uppercase tracking-[0.9px] mb-2">{t('supplier.participants.requirements')}</p>
                      {hasReq(p.allergies)           && <p className="text-[12px] text-[#92400E]"><strong>{t('supplier.participants.allergies')}</strong> {p.allergies}</p>}
                      {hasReq(p.dietaryRequirements) && <p className="text-[12px] text-[#374151]"><strong>{t('supplier.participants.dietary')}</strong> {p.dietaryRequirements}</p>}
                      {hasReq(p.conditions)          && <p className="text-[12px] text-[#374151]"><strong>{t('supplier.participants.medical')}</strong> {p.conditions}</p>}
                      {hasReq(p.safetyInfo)          && <p className="text-[12px] text-[#92400E]"><strong>{t('supplier.participants.safety')}</strong> {p.safetyInfo}</p>}
                    </div>
                  )}

                  {(p.contactPhone || p.contactEmail) && (
                    <div>
                      <p className="text-[10px] font-bold text-[#6B7280] uppercase tracking-[0.9px] mb-2">{t('supplier.participants.contact')}</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <PRow label={t('supplier.participants.phone')} value={p.contactPhone} />
                        <PRow label={t('supplier.participants.email')} value={p.contactEmail} />
                      </div>
                    </div>
                  )}

                  {(p.guardianName || p.guardianPhone || p.guardianEmail) && (
                    <div>
                      <p className="text-[10px] font-bold text-[#6B7280] uppercase tracking-[0.9px] mb-2">{t('supplier.participants.guardian')}</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <PRow label={t('supplier.participants.name')}  value={p.guardianName} />
                        <PRow label={t('supplier.participants.phone')} value={p.guardianPhone} />
                        <PRow label={t('supplier.participants.email')} value={p.guardianEmail} />
                      </div>
                    </div>
                  )}

                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Itinerary day view ────────────────────────────────────────────────────────

function ItineraryView({ items, filterType, onUploadTicket, onRemoveTicket, uploadingTicketId, matches = null, departureDate = null }) {
  const t = useT();
  if (!items || items.length === 0) return <p className="text-[13px] text-[#9CA3AF]">{t('supplier.itinerary.none')}</p>;

  const filtered = filterType ? items.filter((i) => (i.type || '').toLowerCase() === filterType) : items;
  if (filtered.length === 0) return <p className="text-[13px] text-[#9CA3AF]">{t('supplier.itinerary.noneToDisplay')}</p>;

  const byDay = {};
  for (const item of filtered) {
    const key = item.day ?? (item.dayIdx != null ? item.dayIdx + 1 : 0);
    if (!byDay[key]) byDay[key] = [];
    byDay[key].push(item);
  }

  return (
    <div className="space-y-4">
      {Object.keys(byDay)
        .sort((a, b) => Number(a) - Number(b))
        .map((dayKey) => (
          <div key={dayKey}>
            <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-2">
              {dayLabel(departureDate, Number(dayKey) || null, t)}
            </p>
            <div className="space-y-1.5">
              {byDay[dayKey]
                .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                .map((item) => (
                  <div key={item.id} className="flex items-start gap-3 bg-[#F9FAFB] rounded-[8px] px-4 py-3">
                    <p className="text-[12px] font-semibold text-[#374151] min-w-[48px] flex-shrink-0">{item.time || '—'}</p>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-[#111827]">{t.value('title', item.title) || activityName(item.activityType, t)}</p>
                      {item.activityType && item.title && item.activityType !== item.title && (
                        <p className="text-[11.5px] text-[#6B7280]">{activityName(item.activityType, t)}</p>
                      )}
                      {item.endTime && <p className="text-[11.5px] text-[#9CA3AF]">{t('supplier.until', { time: item.endTime })}</p>}
                      {item.notes && <p className="text-[12px] text-[#9CA3AF] mt-0.5">{item.notes}</p>}
                      {matches && (
                        <ActivityMatches
                          item={item}
                          fixturesById={matches.fixturesById}
                          teamsById={matches.teamsById}
                          departureDate={matches.departureDate}
                          size="sm"
                        />
                      )}
                    </div>
                    {onUploadTicket && (
                      <div className="flex-shrink-0 flex flex-col items-end gap-1">
                        {item.ticketUrl ? (
                          <>
                            <a
                              href={item.ticketUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[6px] text-[10px] font-semibold bg-[#F0FDF4] text-[#16A34A] border border-[#BBF7D0] hover:bg-[#DCFCE7] transition whitespace-nowrap"
                            >
                              {t('supplier.itinerary.ticket')}
                            </a>
                            <div className="flex gap-1">
                              <button
                                onClick={() => onUploadTicket(item.id)}
                                disabled={uploadingTicketId === item.id}
                                className="px-1.5 py-0.5 rounded-[5px] text-[10px] font-semibold text-[#6B7280] border border-[#D1D5DB] hover:text-pb hover:border-pb transition disabled:opacity-40"
                              >
                                {t('supplier.itinerary.replace')}
                              </button>
                              <button
                                onClick={() => onRemoveTicket(item.id, item.ticketStoragePath)}
                                className="px-1.5 py-0.5 rounded-[5px] text-[10px] font-semibold text-[#DC2626] border border-[#FCA5A5] hover:bg-[#FEF2F2] transition"
                              >
                                ✕
                              </button>
                            </div>
                          </>
                        ) : (
                          <button
                            onClick={() => onUploadTicket(item.id)}
                            disabled={uploadingTicketId === item.id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[6px] text-[10px] font-semibold text-[#6B7280] border border-[#E5E7EB] hover:text-pb hover:border-pb transition disabled:opacity-40"
                          >
                            {uploadingTicketId === item.id ? '…' : t('supplier.itinerary.uploadTicket')}
                          </button>
                        )}
                      </div>
                    )}
                    {!onUploadTicket && item.ticketUrl && (
                      <a
                        href={item.ticketUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-[6px] text-[10px] font-semibold bg-[#F0FDF4] text-[#16A34A] border border-[#BBF7D0] hover:bg-[#DCFCE7] transition whitespace-nowrap"
                      >
                        {t('supplier.itinerary.ticket')}
                      </a>
                    )}
                  </div>
                ))}
            </div>
          </div>
        ))}
    </div>
  );
}

// ── Food orders view ──────────────────────────────────────────────────────────

function FoodOrdersView({ orders }) {
  const t = useT();
  if (orders.length === 0) return <p className="text-[13px] text-[#9CA3AF]">{t('supplier.food.none')}</p>;

  return (
    <div className="space-y-4">
      {orders.map((order) => (
        <div key={order.id} className="bg-[#F9FAFB] rounded-[10px] border border-[#E5E7EB] p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[13px] font-bold text-[#111827]">{order.date || t('supplier.food.dateTbc')}</p>
            <span className="text-[11px] font-semibold text-[#6B7280]">{t('supplier.food.groupOf', { n: order.groupSize || '—' })}</span>
          </div>
          {(order.items || []).length > 0 && (
            <div className="space-y-1">
              {order.items.map((item, i) => (
                <div key={i} className="flex items-baseline gap-2">
                  <span className="text-[13px] font-semibold text-[#374151] min-w-[30px]">{item.quantity}×</span>
                  <span className="text-[13px] text-[#111827]">{item.name}</span>
                  {item.notes && <span className="text-[12px] text-[#9CA3AF] italic">({item.notes})</span>}
                </div>
              ))}
            </div>
          )}
          {order.notes && <p className="text-[12px] text-[#9CA3AF] mt-2 italic">{order.notes}</p>}
        </div>
      ))}
    </div>
  );
}

// ── Rooming view ─────────────────────────────────────────────────────────────

function RoomingView({ rooming }) {
  const t = useT();
  const rooms = [...(rooming?.rooms || []), ...(rooming?.staffRooms || []).map((r) => ({ ...r, staff: true }))];
  if (rooms.length === 0) return <p className="text-[13px] text-[#9CA3AF]">{t('supplier.rooming.none')}</p>;

  return (
    <div className="space-y-2">
      {rooms.map((room, i) => (
        <div key={i} className="bg-[#F9FAFB] rounded-[8px] px-4 py-3">
          <p className="text-[13px] font-bold text-[#111827]">
            {room.label || t('supplier.rooming.room', { n: i + 1 })}
            {room.staff && <span className="ml-2 text-[10.5px] font-semibold text-[#6B7280]">{t('supplier.rooming.staff')}</span>}
            {room.beds ? <span className="ml-2 text-[11px] font-normal text-[#9CA3AF]">{t('supplier.rooming.beds', { n: room.beds })}</span> : null}
          </p>
          {room.occupants.length > 0 ? (
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
              {room.occupants.map((name, j) => (
                <span key={j} className="text-[12px] text-[#374151]">{name}</span>
              ))}
            </div>
          ) : (
            <p className="text-[12px] text-[#9CA3AF] mt-0.5">{t('supplier.rooming.empty')}</p>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Bottom nav icons ──────────────────────────────────────────────────────────

function NavIconBooking() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
      <rect x="9" y="3" width="6" height="4" rx="1" ry="1"/>
      <path d="M9 12l2 2 4-4"/>
    </svg>
  );
}
function NavIconTour() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  );
}
function NavIconParticipants() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
}
function NavIconItinerary() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
      <line x1="8" y1="14" x2="16" y2="14"/>
      <line x1="8" y1="18" x2="13" y2="18"/>
    </svg>
  );
}
function NavIconRooming() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  );
}
function NavIconFood() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 2v7c0 1.1.9 2 2 2s2-.9 2-2V2"/>
      <path d="M7 2v20"/>
      <path d="M21 15V2c-2.8 0-5 2.2-5 5v6c0 1.1.9 2 2 2h3z"/>
      <path d="M21 15v7"/>
    </svg>
  );
}
function NavIconContractor() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
    </svg>
  );
}

// ── Tab definitions ───────────────────────────────────────────────────────────

const ALL_TABS = [
  { key: 'booking',      icon: <NavIconBooking /> },
  { key: 'tour',         icon: <NavIconTour /> },
  { key: 'participants', icon: <NavIconParticipants /> },
  { key: 'itinerary',    icon: <NavIconItinerary /> },
  { key: 'rooming',      icon: <NavIconRooming /> },
  { key: 'food',         icon: <NavIconFood /> },
  { key: 'contractor',   icon: <NavIconContractor /> },
];

// ── Main component ────────────────────────────────────────────────────────────
// Everything here comes from the supplier's own view of the tour, built
// server-side from their permissions (functions/supplierViews.js). The tour's
// own records are not readable by suppliers.

export default function SupplierTourDetail({ requestId: requestIdProp } = {}) {
  const params = useParams();
  const requestId = requestIdProp || params.requestId;
  const navigate = usePreviewNavigate();
  const t = useT();

  const { loading, views } = useSupplierViews(requestId);
  const view = useMemo(() => mergeViews(views), [views]);
  const [activeTab, setActiveTab] = useState('booking');
  const [answeredApprovals, setAnsweredApprovals] = useState({}); // hidden until the view catches up
  const [uploadingTicketId, setUploadingTicketId] = useState(null);
  const ticketInputRef = useRef(null);
  const pendingTicketItemId = useRef(null);

  const permissions = useMemo(() => ({ ...DEFAULT_PERMISSIONS, ...(view?.permissions || {}) }), [view]);
  const tour = view?.tour || {};
  const myItems = useMemo(() => view?.myItems || [], [view]);

  // Teams with ages and kit, from the squads in the view.
  const teams = useMemo(() => teamsWithAges(view?.teams || [], view?.squads || [], tour), [view]); // eslint-disable-line react-hooks/exhaustive-deps
  const matches = useMemo(() => (permissions.fixtureInfo ? {
    fixturesById: Object.fromEntries((view?.fixtures || []).map((f) => [f.id, f])),
    teamsById: Object.fromEntries(teams.map((t) => [t.id, t])),
    departureDate: tour.departureDate,
  } : null), [permissions.fixtureInfo, view, teams, tour.departureDate]);

  const approvals = (view?.approvals || [])
    .filter((a) => !answeredApprovals[a.id])
    .map((a) => ({ ...a, requestId, items: myItems.filter((i) => a.itemIds.includes(i.id)) }));
  const bookings = view?.bookings || [];

  const visibleTabs = useMemo(() => ALL_TABS.filter((tab) => {
    if (tab.key === 'booking' || tab.key === 'itinerary') return true;
    if (tab.key === 'tour')         return permissions.tourOverview || permissions.tourLeaderInfo || permissions.teamInfo || permissions.teamLists;
    if (tab.key === 'participants') return permissions.participantInfo || permissions.participantPassports;
    if (tab.key === 'rooming')      return permissions.rooming;
    if (tab.key === 'food')         return permissions.sandwichOrderResponses || permissions.tgbOrderResponses || permissions.belaCafeResponses;
    if (tab.key === 'contractor')   return permissions.canEditItinerary || permissions.canLogExpenses;
    return false;
  }), [permissions]);

  useEffect(() => {
    if (!visibleTabs.find((t) => t.key === activeTab)) setActiveTab('booking');
  }, [visibleTabs, activeTab]);

  const hasPendingAction = bookings.some((b) => b.status === 'pending') || approvals.length > 0;

  function handleTicketUploadClick(itemId) {
    pendingTicketItemId.current = itemId;
    ticketInputRef.current?.click();
  }

  async function handleTicketFileChange(e) {
    const file = e.target.files?.[0];
    const itemId = pendingTicketItemId.current;
    e.target.value = '';
    if (!file || !itemId) return;
    setUploadingTicketId(itemId);
    try {
      const path = `requests/${requestId}/tickets/${itemId}`;
      const sRef = storageRef(storage, path);
      await uploadBytes(sRef, file);
      const url = await getDownloadURL(sRef);
      await updateDoc(doc(db, 'requests', requestId, 'itinerary', itemId), {
        ticketUrl: url, ticketStoragePath: path, updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Ticket upload failed:', err);
      alert(t('supplier.itinerary.uploadFailed'));
    } finally {
      setUploadingTicketId(null);
      pendingTicketItemId.current = null;
    }
  }

  async function handleTicketRemove(itemId, storagePath) {
    if (!window.confirm(t('supplier.itinerary.removeConfirm'))) return;
    try {
      if (storagePath) await deleteObject(storageRef(storage, storagePath)).catch(() => {});
      await updateDoc(doc(db, 'requests', requestId, 'itinerary', itemId), {
        ticketUrl: null, ticketStoragePath: null, updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Ticket remove failed:', err);
      alert(t('supplier.itinerary.removeFailed'));
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <p className="text-[13px] text-[#9CA3AF]">{t('supplier.loading')}</p>
      </div>
    );
  }

  if (!view) {
    return (
      <div className="min-h-screen bg-page flex flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-[14px] font-semibold text-[#111827]">{t('supplier.unavailableTitle')}</p>
        <p className="text-[13px] text-[#6B7280] max-w-[360px]">{t('supplier.unavailableBody')}</p>
        <button onClick={() => navigate('/supplier')} className="text-[13px] font-semibold text-pb hover:text-navy">← {t('supplier.myTours')}</button>
      </div>
    );
  }

  const tourTitle = tour.tourName || `${t.value('sport', tour.sport) || ''} — ${tour.destination || ''}`;
  const overview = view.overview || {};
  const players = view.players || {};
  const showPlayers = permissions.teamInfo || permissions.teamLists;
  const squadById = Object.fromEntries(squadsWithIds(view.squads || []).map((sq) => [sq.id, sq]));
  // Squads not linked to a team still show their Team Info card.
  const looseSquads = permissions.teamInfo
    ? squadsWithIds(view.squads || []).filter((sq) => !teams.some((t) => t.squadId === sq.id))
    : [];

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-[calc(80px+env(safe-area-inset-bottom,0px))] overflow-x-hidden">

      {/* Sticky header */}
      <div className="sticky top-0 z-30 bg-white border-b border-[#E5E7EB] px-4 pt-2 pb-3">
        <nav className="flex items-center gap-1 text-[11px] mb-1.5" aria-label="Breadcrumb">
          <button
            onClick={() => navigate('/supplier')}
            className="text-[#9CA3AF] hover:text-[#6B7280] transition flex items-center gap-1"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            {t('supplier.myTours')}
          </button>
          <svg className="w-2.5 h-2.5 text-[#D1D5DB]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-[#374151] font-medium truncate">{tourTitle}</span>
        </nav>
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-[15px] font-bold text-[#111827] truncate">{tourTitle}</h2>
            <p className="text-[12px] text-[#9CA3AF] truncate">
              {formatDate(tour.departureDate)} – {formatDate(tour.returnDate)}
              {tour.totalTravellers ? ` · ${t('supplier.pax', { n: tour.totalTravellers })}` : ''}
            </p>
          </div>
          <LanguageSwitch tone="light" />
        </div>
      </div>

      {/* Tab content */}
      <div className="px-4 py-5 max-w-3xl mx-auto">

        {/* Booking tab */}
        {activeTab === 'booking' && (
          <div className="space-y-5">
            {approvals.length > 0 && (
              <SectionCard title={t('supplier.approvalRequired')}>
                <div className="space-y-6">
                  {approvals.map((token) => (
                    <ItineraryApprovalSection
                      key={token.id}
                      token={token}
                      onDone={() => setAnsweredApprovals((prev) => ({ ...prev, [token.id]: true }))}
                      departureDate={tour.departureDate}
                    />
                  ))}
                </div>
              </SectionCard>
            )}

            {bookings.length > 0 && (
              <SectionCard title={bookings.some((a) => a.status === 'pending') ? t('supplier.bookingRequired') : t('supplier.bookingTitle')}>
                <div className="space-y-3">
                  {bookings.map((a) => <BookingAssignmentRow key={a.id} assignment={{ ...a, requestId }} />)}
                </div>
              </SectionCard>
            )}

            <SectionCard title={t('supplier.yourActivities')}>
              {myItems.length === 0 ? (
                <p className="text-[13px] text-[#9CA3AF]">{t('supplier.noActivities')}</p>
              ) : (
                <div className="space-y-1.5">
                  {[...myItems]
                    .sort((a, b) => (a.day || 0) - (b.day || 0) || (a.time || '').localeCompare(b.time || ''))
                    .map((item) => {
                      // "Pending" only means waiting on the supplier once MST has sent the request.
                      const requested = approvals.some((a) => a.itemIds.includes(item.id));
                      const st = item.approvalStatus === 'pending' && !requested
                        ? { key: 'not_sent', ...APPROVAL_LABELS.not_sent }
                        : APPROVAL_LABELS[item.approvalStatus] ? { key: item.approvalStatus, ...APPROVAL_LABELS[item.approvalStatus] } : null;
                      return (
                        <div key={item.id} className="flex items-center gap-3 bg-[#F9FAFB] rounded-[8px] px-4 py-2.5">
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold text-[#111827]">{t.value('title', item.title) || activityName(item.activityType, t)}</p>
                            <p className="text-[12px] text-[#6B7280]">
                              {item.day ? (dayDate(tour.departureDate, item.day, t) || t('supplier.day', { n: item.day })) : t('supplier.dayTbc')}{item.time ? ` · ${item.time}` : ''}
                              {item.activityType && item.title && item.activityType !== item.title ? ` · ${activityName(item.activityType, t)}` : ''}
                            </p>
                          </div>
                          {st && <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full text-center flex-shrink-0 max-w-[120px] ${st.cls}`}>{t(`supplier.status.${st.key}`)}</span>}
                        </div>
                      );
                    })}
                  <button onClick={() => setActiveTab('itinerary')} className="text-[12px] font-semibold text-pb hover:text-navy pt-1">
                    {t('supplier.fullDetails')}
                  </button>
                </div>
              )}
            </SectionCard>
          </div>
        )}

        {/* Tour Info tab */}
        {activeTab === 'tour' && (
          <div className="space-y-5">
            {permissions.tourOverview && (
              <SectionCard title={t('supplier.overview.title')}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <Field label={t('supplier.overview.sport')} value={t.value('sport', tour.sport)} />
                  <Field label={t('supplier.overview.destination')} value={tour.destination} />
                  <Field label={t('supplier.overview.departure')} value={formatDate(tour.departureDate)} />
                  <Field label={t('supplier.overview.return')} value={formatDate(tour.returnDate)} />
                  <Field label={t('supplier.overview.groupType')} value={overview.groupType} />
                  <Field label={t('supplier.overview.totalTravellers')} value={tour.totalTravellers ? `${tour.totalTravellers}` : null} />
                  <Field label={t('supplier.overview.players')} value={overview.participants ? `${overview.participants}` : null} />
                  {overview.participantsMale != null && <Field label={t('supplier.overview.male')} value={`${overview.participantsMale}`} />}
                  {overview.participantsFemale != null && <Field label={t('supplier.overview.female')} value={`${overview.participantsFemale}`} />}
                </div>
              </SectionCard>
            )}

            {permissions.tourLeaderInfo && (
              <SectionCard title={(view.tourLeaders || []).length === 1 ? t('supplier.leaders.one') : t('supplier.leaders.many')}>
                {(view.tourLeaders || []).length === 0 ? (
                  <p className="text-[13px] text-[#9CA3AF]">{t('supplier.leaders.none')}</p>
                ) : (
                  <div className="space-y-3">
                    {view.tourLeaders.map((l, i) => (
                      <div key={i} className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <Field label={t('supplier.leaders.name')} value={l.name} />
                        <Field label={t('supplier.leaders.email')} value={l.email} />
                        <Field label={t('supplier.leaders.phone')} value={l.phone} />
                        {l.teams?.length > 0 && <Field label={t('supplier.leaders.leads')} value={l.teams.join(', ')} />}
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            )}

            {(permissions.teamInfo || permissions.teamLists) && (
              <>
                {permissions.teamInfo && view.teamInfoSummary && (view.teamInfoSummary.numSquads != null || view.teamInfoSummary.goalkeepersTotal != null) && (
                  <SectionCard title={t('supplier.teams.info')}>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {view.teamInfoSummary.numSquads != null && <Field label={t('supplier.teams.squads')} value={`${view.teamInfoSummary.numSquads}`} />}
                      {view.teamInfoSummary.goalkeepersTotal != null && <Field label={t('supplier.teams.goalkeepers')} value={`${view.teamInfoSummary.goalkeepersTotal}`} />}
                    </div>
                  </SectionCard>
                )}
                {teams.length === 0 && looseSquads.length === 0 && (
                  <SectionCard title={t('supplier.teams.teams')}>
                    <p className="text-[13px] text-[#9CA3AF]">
                      {myItems.some((i) => /^fixtures?$/i.test(i.activityType || ''))
                        ? t('supplier.teams.noLinked')
                        : t('supplier.teams.notSubmitted')}
                    </p>
                  </SectionCard>
                )}
                {teams.map((team, i) => {
                  const squad = team.squadId && squadById[team.squadId];
                  const list = players[team.id] || [];
                  return (
                    <div key={team.id} className="space-y-2">
                      {permissions.teamInfo && squad ? (
                        <SquadCard squad={{ ...squad, label: team.name || squad.label }} index={i} request={tour} uidPrefix="sup-kit" showFinder={false} />
                      ) : (
                        <p className="text-[13px] font-bold text-[#111827] px-1">{team.name}{team.ageLabel ? <span className="font-normal text-[#6B7280]"> · {team.ageLabel}</span> : null}</p>
                      )}
                      {showPlayers && (
                        <SectionCard title={t('supplier.teams.playersN', { team: team.name, n: list.length })}>
                          {list.length === 0 ? (
                            <p className="text-[13px] text-[#9CA3AF]">{t('supplier.teams.noPlayers')}</p>
                          ) : (
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                              {list.map((p, j) => (
                                <li key={j} className="text-[13px] text-[#374151]">
                                  {p.name}
                                  {formatDob(p) && <span className="text-[#6B7280]"> ({formatDob(p)})</span>}
                                  {p.travellerType && p.travellerType.toLowerCase() !== 'player' && <span className="text-[11px] text-[#9CA3AF]"> · {p.travellerType}</span>}
                                </li>
                              ))}
                            </ul>
                          )}
                        </SectionCard>
                      )}
                    </div>
                  );
                })}
                {looseSquads.map((sq, i) => (
                  <SquadCard key={sq.id} squad={sq} index={teams.length + i} request={tour} uidPrefix="sup-kit-loose" showFinder={false} />
                ))}
              </>
            )}
          </div>
        )}

        {/* Participants tab */}
        {activeTab === 'participants' && (permissions.participantInfo || permissions.participantPassports) && (
          <SectionCard title={t('supplier.participants.title')}>
            <ParticipantsList participants={view.participants || []} tourName={tourTitle} withPassports={permissions.participantPassports} />
          </SectionCard>
        )}

        {/* Itinerary tab */}
        {activeTab === 'itinerary' && (
          <div className="space-y-5">
            <SectionCard title={t('supplier.yourActivities')}>
              <p className="text-[11px] text-[#9CA3AF] mb-3">{t('supplier.itinerary.yourActivitiesHint')}</p>
              <ItineraryView
                items={myItems}
                matches={matches}
                departureDate={tour.departureDate}
                onUploadTicket={handleTicketUploadClick}
                onRemoveTicket={handleTicketRemove}
                uploadingTicketId={uploadingTicketId}
              />
              <input
                type="file"
                accept="application/pdf,image/*"
                ref={ticketInputRef}
                className="hidden"
                onChange={handleTicketFileChange}
              />
            </SectionCard>

            {permissions.generalItinerary && (
              <SectionCard title={t('supplier.itinerary.full')}>
                <ItineraryView items={view.itinerary || []} departureDate={tour.departureDate} />
              </SectionCard>
            )}
          </div>
        )}

        {/* Rooming tab */}
        {activeTab === 'rooming' && permissions.rooming && (
          <SectionCard title={t('supplier.rooming.title')}>
            <RoomingView rooming={view.rooming} />
          </SectionCard>
        )}

        {/* Food tab */}
        {activeTab === 'food' && (
          <div className="space-y-5">
            {permissions.sandwichOrderResponses && (
              <SectionCard title={t('supplier.food.sandwich')}>
                <FoodOrdersView orders={view.foodOrders?.sandwich || []} />
              </SectionCard>
            )}
            {permissions.tgbOrderResponses && (
              <SectionCard title={t('supplier.food.tgb')}>
                <FoodOrdersView orders={view.foodOrders?.tgb || []} />
              </SectionCard>
            )}
            {permissions.belaCafeResponses && (
              <SectionCard title={t('supplier.food.bela')}>
                <FoodOrdersView orders={view.foodOrders?.belaCafe || []} />
              </SectionCard>
            )}
          </div>
        )}

        {/* Contractor tab */}
        {activeTab === 'contractor' && (permissions.canEditItinerary || permissions.canLogExpenses) && (
          <ContractorSection
            requestId={requestId}
            items={myItems}
            permissions={permissions}
          />
        )}

      </div>

      {/* Fixed bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E5E7EB] flex pb-safe">
        {visibleTabs.map((tab) => {
          const active = activeTab === tab.key;
          const showBadge = tab.key === 'booking' && hasPendingAction;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-3 min-h-[56px] transition-colors relative ${active ? 'text-pb' : 'text-[#9CA3AF] hover:text-[#6B7280]'}`}
            >
              <span className={`transition-colors relative ${active ? 'text-pb' : 'text-[#9CA3AF]'}`}>
                {tab.icon}
                {showBadge && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-dr" />
                )}
              </span>
              <span className={`text-[10px] font-semibold tracking-wide ${active ? 'text-pb' : 'text-[#9CA3AF]'}`}>
                {t(`supplier.tabs.${tab.key}`)}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

// Badge colours per approval status; the words are supplier.status.* in the dictionaries.
const APPROVAL_LABELS = {
  pending:      { cls: 'bg-[#FFF7ED] text-[#92400E]' },
  not_sent:     { cls: 'bg-[#F3F4F6] text-[#6B7280]' },
  approved:     { cls: 'bg-[#ECFDF5] text-[#065F46]' },
  overridden:   { cls: 'bg-[#ECFDF5] text-[#065F46]' },
  not_required: { cls: 'bg-[#ECFDF5] text-[#065F46]' },
  declined:     { cls: 'bg-[#FEE2E2] text-[#991B1B]' },
  question:     { cls: 'bg-[#EFF6FF] text-[#1D4ED8]' },
};

// ── Contractor section (canEditItinerary + canLogExpenses) ────────────────────

function ContractorSection({ requestId, items, permissions }) {
  const t = useT();
  const [expenseForm, setExpenseForm] = useState({ description: '', amount: '', category: 'Fixtures' });
  const [savingExpense, setSavingExpense] = useState(false);
  const [expenseSaved, setExpenseSaved] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemEdits, setItemEdits] = useState({});
  const [savingItem, setSavingItem] = useState(false);

  // Their own activities only; the rules refuse edits to anyone else's.
  const fixtureItems = items;

  async function saveExpense(e) {
    e.preventDefault();
    if (!expenseForm.description.trim() || !expenseForm.amount) return;
    setSavingExpense(true);
    try {
      await addDoc(collection(db, 'requests', requestId, 'expenses'), {
        description: expenseForm.description.trim(),
        amount: Number(expenseForm.amount),
        category: expenseForm.category,
        addedBy: 'contractor',
        createdAt: serverTimestamp(),
      });
      setExpenseForm({ description: '', amount: '', category: 'Fixtures' });
      setExpenseSaved(true);
      setTimeout(() => setExpenseSaved(false), 3000);
    } catch (err) {
      console.error(err);
      alert(t('supplier.contractor.expenseFailed'));
    } finally {
      setSavingExpense(false);
    }
  }

  async function saveItemEdits(itemId) {
    const edits = itemEdits[itemId];
    if (!edits) return;
    setSavingItem(true);
    try {
      const updates = {};
      if (edits.title !== undefined) updates.title = edits.title;
      if (edits.notes !== undefined) updates.notes = edits.notes;
      if (edits.time !== undefined) updates.time = edits.time;
      await updateDoc(doc(db, 'requests', requestId, 'itinerary', itemId), updates);
      setEditingItem(null);
    } catch (err) {
      console.error(err);
      alert(t('supplier.contractor.saveFailed'));
    } finally {
      setSavingItem(false);
    }
  }

  return (
    <div className="space-y-5">
      {permissions.canEditItinerary && fixtureItems.length > 0 && (
        <SectionCard title={t('supplier.contractor.updateTitle')}>
          <div className="space-y-3">
            {fixtureItems.map((item) => (
              <div key={item.id} className="border border-[#E5E7EB] rounded-[10px] p-4">
                {editingItem === item.id ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">{t('supplier.contractor.titleField')}</label>
                      <input
                        value={itemEdits[item.id]?.title ?? item.title ?? ''}
                        onChange={(e) => setItemEdits((p) => ({ ...p, [item.id]: { ...p[item.id], title: e.target.value } }))}
                        className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">{t('supplier.contractor.time')}</label>
                      <input
                        value={itemEdits[item.id]?.time ?? item.time ?? ''}
                        onChange={(e) => setItemEdits((p) => ({ ...p, [item.id]: { ...p[item.id], time: e.target.value } }))}
                        className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">{t('supplier.contractor.notes')}</label>
                      <textarea
                        value={itemEdits[item.id]?.notes ?? item.notes ?? ''}
                        onChange={(e) => setItemEdits((p) => ({ ...p, [item.id]: { ...p[item.id], notes: e.target.value } }))}
                        rows={3}
                        className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb resize-none"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => saveItemEdits(item.id)} disabled={savingItem}
                        className="px-[16px] py-[8px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60">
                        {savingItem ? t('supplier.contractor.saving') : t('supplier.contractor.save')}
                      </button>
                      <button onClick={() => setEditingItem(null)}
                        className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151] transition">{t('supplier.contractor.cancel')}</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[13px] font-bold text-[#111827]">{t.value('title', item.title) || activityName(item.activityType, t)}</p>
                      <p className="text-[12px] text-[#9CA3AF] mt-0.5">{item.time || '—'}</p>
                      {item.notes && <p className="text-[12px] text-[#374151] mt-0.5 italic">{item.notes}</p>}
                    </div>
                    <button
                      onClick={() => {
                        setEditingItem(item.id);
                        setItemEdits((p) => ({ ...p, [item.id]: { title: item.title, time: item.time, notes: item.notes } }));
                      }}
                      className="text-[12.5px] font-bold text-pb hover:text-navy transition whitespace-nowrap flex-shrink-0"
                    >
                      {t('supplier.contractor.edit')}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {permissions.canLogExpenses && (
        <SectionCard title={t('supplier.contractor.logTitle')}>
          <form onSubmit={saveExpense} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">{t('supplier.contractor.description')}</label>
                <input
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder={t('supplier.contractor.descPh')}
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div>
                <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">{t('supplier.contractor.amount')}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm((p) => ({ ...p, amount: e.target.value }))}
                  placeholder="0.00"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button type="submit" disabled={savingExpense || !expenseForm.description.trim() || !expenseForm.amount}
                className="px-[16px] py-[8px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60">
                {savingExpense ? t('supplier.contractor.saving') : t('supplier.contractor.log')}
              </button>
              {expenseSaved && <span className="text-[12px] text-sg font-semibold">{t('supplier.contractor.saved')}</span>}
            </div>
          </form>
        </SectionCard>
      )}
    </div>
  );
}
